import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Tag } from "@/components/admin/tag";
import { useMomoLive, usePaymentsList } from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";
import { paymentsApi } from "@/lib/payments/payments-api";
import type { PaymentMethod, PaymentSearchParams } from "@/lib/payments/types";
import { statusPayment } from "@/lib/status";

const PAGE_SIZE = 25;

interface TabDef {
	key: string;
	label: string;
	filter: Partial<PaymentSearchParams>;
}

const TABS: TabDef[] = [
	{ key: "all", label: "Toutes", filter: {} },
	{ key: "momo", label: "MoMo", filter: { method: ["momo_mtn"] } },
	{ key: "om", label: "OM", filter: { method: ["momo_om"] } },
	{ key: "card", label: "Carte", filter: { method: ["card"] } },
	{ key: "cod", label: "COD", filter: { method: ["cash_cod"] } },
	{
		key: "pending",
		label: "En attente",
		filter: { status: ["PENDING", "INITIATED"] },
	},
	{ key: "failed", label: "Échecs", filter: { status: ["FAILED", "EXPIRED"] } },
	{
		key: "refunded",
		label: "Remboursés",
		filter: { status: ["REFUNDED", "PARTIALLY_REFUNDED"] },
	},
	{
		key: "disputed",
		label: "Litiges",
		filter: { status: ["DISPUTED", "CHARGEBACK"] },
	},
];

const METHOD_LABEL: Record<PaymentMethod, string> = {
	momo_mtn: "MoMo MTN",
	momo_om: "OM",
	cash_cod: "COD",
	bank_transfer: "Virement",
	card: "Carte",
	wallet: "Avoir",
};

export function AdminPaymentsPage() {
	const navigate = useNavigate();
	const [tab, setTab] = useState("all");
	const [page, setPage] = useState(1);
	const [query, setQuery] = useState("");

	const filter: PaymentSearchParams = useMemo(() => {
		const def = TABS.find((t) => t.key === tab)?.filter ?? {};
		return {
			...def,
			query: query.trim() || undefined,
			page,
			pageSize: PAGE_SIZE,
		};
	}, [tab, page, query]);

	const { data, isLoading } = usePaymentsList(filter);
	const { data: live } = useMomoLive(
		tab === "all" || tab === "momo" || tab === "om",
	);

	const items = data?.items ?? [];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const kpis = useMemo(() => {
		const todaySuccess = live?.kpis?.today_success_amount ?? 0;
		const todayCount = live?.kpis?.today_success_count ?? 0;
		const pending = live?.pending?.length ?? 0;
		const fails = live?.kpis?.failures_last1h ?? 0;
		return { todaySuccess, todayCount, pending, fails };
	}, [live]);

	const exportCsv = () => {
		const url = paymentsApi.exportCsvUrl(filter);
		const a = document.createElement("a");
		a.href = url;
		a.download = `paiements-${Date.now()}.csv`;
		a.click();
	};

	return (
		<>
			<PageHead
				title="Paiements"
				sub="Transactions Mobile Money, carte et COD · réconciliation J+1"
				actions={
					<>
						<AdminButton
							variant="ghost"
							onClick={() => navigate("/paiements/momo-live")}
						>
							<AdminIcon n="refresh" s={14} />
							Live MoMo/OM
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() => navigate("/paiements/rapprochement")}
						>
							<AdminIcon n="check" s={14} />
							Rapprochement
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() => navigate("/paiements/litiges")}
						>
							<AdminIcon n="alert" s={14} />
							Litiges
						</AdminButton>
						<AdminButton variant="ghost" onClick={exportCsv}>
							<AdminIcon n="download" s={14} />
							Export comptable
						</AdminButton>
					</>
				}
			/>

			<div className="kpi-grid">
				<div className="kpi success">
					<div className="kpi-l">Encaissé aujourd'hui</div>
					<div className="kpi-v tn">{fmtFCFA(kpis.todaySuccess)}</div>
					<span className="kpi-delta up">{kpis.todayCount} transactions</span>
				</div>
				<div className="kpi">
					<div className="kpi-l">En attente</div>
					<div className="kpi-v tn">{kpis.pending}</div>
					<span className="kpi-delta neutral">PENDING actifs</span>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">Échecs (dernière heure)</div>
					<div className="kpi-v tn">{kpis.fails}</div>
					<span className="kpi-delta down">Surveillance PSP</span>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">Litiges ouverts</div>
					<div className="kpi-v tn">
						<button
							className="top-btn"
							type="button"
							onClick={() => navigate("/paiements/litiges")}
							style={{ background: "transparent", padding: 0, fontWeight: 700 }}
						>
							Voir
						</button>
					</div>
					<span className="kpi-delta neutral">SLA 5j ouvrés</span>
				</div>
			</div>

			<Card>
				<div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
					<div
						className="filter-bar"
						style={{ marginBottom: 0, flexWrap: "wrap", gap: 6 }}
					>
						{TABS.map((t) => (
							<ChipFilter
								key={t.key}
								on={tab === t.key}
								onClick={() => {
									setTab(t.key);
									setPage(1);
								}}
							>
								{t.label}
							</ChipFilter>
						))}
					</div>
					<input
						className="aform-i"
						type="search"
						placeholder="Référence, MSISDN, ID PSP…"
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setPage(1);
						}}
						style={{ flex: "1 1 220px", minWidth: 180 }}
					/>
				</div>
				{isLoading ? (
					<CardBody>
						<div className="empty">Chargement…</div>
					</CardBody>
				) : items.length === 0 ? (
					<CardBody>
						<div className="empty">Aucun paiement avec ces filtres.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Référence</th>
									<th>Méthode</th>
									<th>Commande</th>
									<th className="right">Brut</th>
									<th className="right">Net</th>
									<th>Statut</th>
									<th>Initiée</th>
									<th>ID PSP</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{items.map((p) => {
									const s = statusPayment(p.status);
									return (
										<tr
											key={p.id}
											onClick={() => navigate(`/paiements/${p.id}`)}
											style={{ cursor: "pointer" }}
										>
											<td>
												<span className="mono">{p.reference}</span>
											</td>
											<td>
												<Tag variant="neutral" noDot>
													{METHOD_LABEL[p.method] ?? p.method}
												</Tag>
											</td>
											<td>
												<span className="mono muted">
													{p.order_id ? p.order_id.slice(0, 8) : "—"}
												</span>
											</td>
											<td className="right tn">
												<strong>{fmtFCFA(p.amount_gross)}</strong>
											</td>
											<td className="right tn muted">
												{fmtFCFA(p.amount_net)}
											</td>
											<td>
												<Tag variant={s.cls}>{s.l}</Tag>
											</td>
											<td className="muted">
												{new Date(p.initiated_at).toLocaleString("fr-FR")}
											</td>
											<td className="mono muted" style={{ fontSize: 11 }}>
												{p.provider_reference ?? "—"}
											</td>
											<td>
												<button
													className="top-btn"
													type="button"
													aria-label="Détail"
													onClick={(e) => {
														e.stopPropagation();
														navigate(`/paiements/${p.id}`);
													}}
												>
													<AdminIcon n="chevR" s={14} />
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
				{totalPages > 1 && (
					<Pagination
						from={items.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}
						to={(page - 1) * PAGE_SIZE + items.length}
						total={total}
						current={page}
						totalPages={totalPages}
						onChange={setPage}
					/>
				)}
			</Card>
		</>
	);
}
